import { RoleHelper, formatExpiryDates, splitByWarehouseRatio } from "src/helpers";
import { formatFloatWithTwoDecimals } from "src/utils";
import { ProductDiscrepanciesColumnHeader } from "src/enums";
import { OrderProductDiscrepancy, TableColumn } from "src/types";
import { useAuthorisation } from "src/context";
import { TextWithLineBreaks } from "src/components";
import { Table } from "src/components";

interface ProductDiscrepancyTableProps {
  orderProductDiscrepancies: OrderProductDiscrepancy[];
}

const diffClassName = (diff: number | undefined): string => {
  if (diff !== undefined && diff < 0) return "text-error-600";
  if (diff !== undefined && diff > 0) return "text-warning-600";
  return "text-secondary-900";
};

const renderBreakdownHeader = (label: string) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-xs">{label}</span>
    <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
      <span className="text-violet-300">UK</span>
      <span className="text-orange-300">EU</span>
    </div>
  </div>
);

const renderBreakdownCell = (
  total: number | undefined,
  uk: number | undefined,
  eu: number | undefined,
  totalClassName: string
) => {
  const hasSplit = uk !== undefined || eu !== undefined;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`font-semibold tabular-nums text-sm ${totalClassName}`}>
        {total !== undefined ? total : "-"}
      </span>
      {hasSplit && (
        <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
          <span className="text-right text-violet-600">{uk ?? "-"}</span>
          <span className="text-left text-orange-600">{eu ?? "-"}</span>
        </div>
      )}
    </div>
  );
};

const ProductDiscrepancyTable: React.FC<ProductDiscrepancyTableProps> = ({ orderProductDiscrepancies }) => {
  const { userRole } = useAuthorisation();
  const compactCellWidth = { minWidth: "78px" };
  const breakdownCellStyle = { minWidth: "110px", textAlign: "center" as const };

  const showWarehouseBreakdown = orderProductDiscrepancies.some(
    (discrepancy) => discrepancy.ukQty !== undefined || discrepancy.euQty !== undefined
  );

  const columns: TableColumn<ProductDiscrepanciesColumnHeader, OrderProductDiscrepancy>[] = [
    {
      header: ProductDiscrepanciesColumnHeader.PRODUCT_ID,
      column: (row) => <span className="font-medium text-secondary-900">{row.productId}</span>,
      styles: compactCellWidth
    },
    {
      header: ProductDiscrepanciesColumnHeader.PRODUCT_NAME,
      column: (row) => <span className="font-semibold text-secondary-900">{row.productName}</span>,
      styles: { minWidth: "150px" }
    },
    {
      header: ProductDiscrepanciesColumnHeader.PRODUCT_SKU,
      column: (row) => <span className="font-medium text-secondary-900">{row.productSku ?? "-"}</span>,
      styles: compactCellWidth
    },
    ...(showWarehouseBreakdown
      ? [
          {
            header: renderBreakdownHeader(ProductDiscrepanciesColumnHeader.RECEIVED_QTY),
            column: (row: OrderProductDiscrepancy) =>
              renderBreakdownCell(row.receivedQty, row.receivedUkQty, row.receivedEuQty, "text-secondary-900"),
            styles: breakdownCellStyle
          },
          {
            header: renderBreakdownHeader(ProductDiscrepanciesColumnHeader.ORDER_QTY),
            column: (row: OrderProductDiscrepancy) =>
              renderBreakdownCell(row.orderQty, row.ukQty, row.euQty, "text-secondary-900"),
            styles: breakdownCellStyle
          },
          {
            header: renderBreakdownHeader(ProductDiscrepanciesColumnHeader.CONFIRMED_ORDER_QTY),
            column: (row: OrderProductDiscrepancy) => {
              const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(
                row.confirmedQty,
                row.ukQty ?? 0,
                row.euQty ?? 0
              );
              return renderBreakdownCell(row.confirmedQty, confirmedUk, confirmedEu, "text-success-700");
            },
            styles: breakdownCellStyle
          },
          {
            header: renderBreakdownHeader(ProductDiscrepanciesColumnHeader.QTY_DIFFERENCE),
            column: (row: OrderProductDiscrepancy) =>
              renderBreakdownCell(
                row.qtyDifference,
                row.ukQtyDifference,
                row.euQtyDifference,
                diffClassName(row.qtyDifference)
              ),
            styles: breakdownCellStyle
          }
        ]
      : [
          {
            header: ProductDiscrepanciesColumnHeader.RECEIVED_QTY,
            column: (row: OrderProductDiscrepancy) => (
              <span className="font-semibold text-secondary-900">{row.receivedQty?.toString() ?? "-"}</span>
            ),
            styles: compactCellWidth
          },
          {
            header: ProductDiscrepanciesColumnHeader.ORDER_QTY,
            column: (row: OrderProductDiscrepancy) => (
              <span className="font-semibold text-secondary-900">{row.orderQty?.toString() ?? "-"}</span>
            ),
            styles: compactCellWidth
          },
          {
            header: ProductDiscrepanciesColumnHeader.CONFIRMED_ORDER_QTY,
            column: (row: OrderProductDiscrepancy) => (
              <span className="font-semibold text-success-700">{row.confirmedQty?.toString() ?? "-"}</span>
            ),
            styles: compactCellWidth
          },
          {
            header: ProductDiscrepanciesColumnHeader.QTY_DIFFERENCE,
            column: (row: OrderProductDiscrepancy) => (
              <span className={`font-semibold ${diffClassName(row.qtyDifference)}`}>
                {row.qtyDifference?.toString() ?? "-"}
              </span>
            ),
            styles: compactCellWidth
          }
        ])
  ];

  if (RoleHelper.isHealfOperationsTeam(userRole)) {
    columns.push({
      header: ProductDiscrepanciesColumnHeader.COST_VARIANCE,
      column: (row) =>
        row.costVariance ? (
          <span className="font-medium text-secondary-900">£{formatFloatWithTwoDecimals(row.costVariance)}</span>
        ) : (
          <span className="text-secondary-500">-</span>
        )
    });
  }

  columns.push({
    header: ProductDiscrepanciesColumnHeader.DEFECTIVE_EXPIRY_DATE,
    column: (row) =>
      row.defectiveExpiryDates ? (
        <TextWithLineBreaks text={formatExpiryDates(row.defectiveExpiryDates)} />
      ) : (
        <span className="text-secondary-500">-</span>
      )
  });

  return (
    <Table<ProductDiscrepanciesColumnHeader, OrderProductDiscrepancy>
      data={orderProductDiscrepancies}
      columns={columns}
    />
  );
};

export { ProductDiscrepancyTable };
